// External Dependencies
import { doesStateRequireSalesTax } from '@presto-assistant/api_types/utils';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import CheckIcon from '@mui/icons-material/Check';
import type { GridColDef } from '@mui/x-data-grid-pro';

// Internal Dependencies
import { dateColumn, getFullName } from 'utils';
import {
  convertCentsToDollars,
  displayPriceStringFromDollarAmount,
} from 'utils/lib/currency';
import { useGetDistrictTotals } from 'hooks/useGetDistrictTotals';
import { useGetStateOptions } from 'hooks/useGetStateOptions';

// Local Variables
const taxExemptValues = {
  na: 2,
  no: 0,
  yes: 1,
} as const;

const taxExemptOptions = [
  { label: 'Yes', value: taxExemptValues.yes },
  { label: 'No', value: taxExemptValues.no },
  { label: 'N/A', value: taxExemptValues.na },
];

export const useColumns = (extraColumns?: GridColDef[]) => {
  const theme = useTheme();

  const {
    totalDistrictValueInCents,
  } = useGetDistrictTotals();

  const stateOptions = useGetStateOptions();
  const mappedStateOptions = stateOptions?.options.map((option) => ({
    label: option.label,
    value: option.id.toString(),
  }));

  return useMemo(() => {
    const columns: GridColDef<GQL.IDistrictIndex>[] = [
      {
        field: 'label',
        headerName: 'Name',
        minWidth: 352,
      },
      {
        field: 'stateId',
        headerName: 'State',
        minWidth: 120,
        type: 'singleSelect',
        valueOptions: mappedStateOptions,
      },
      {
        field: 'director',
        headerName: 'Admin',
        valueGetter: (params) => getFullName({
          firstName: params.row.dfaMemberFirstName,
          lastName: params.row.dfaMemberLastName,
        }),
        width: 160,
      },
      {
        field: 'dfaMemberEmail',
        headerName: 'Admin Email',
        minWidth: 160,
      },
      {
        field: 'isVerified',
        headerName: 'Verified',
        minWidth: 88,
        type: 'boolean',
      },
      {
        align: 'right',
        field: 'licensePriceInCents',
        headerName: 'License Price',
        maxWidth: 120,
        valueFormatter: (params) =>
          displayPriceStringFromDollarAmount(convertCentsToDollars(params.value)),
      },
      {
        align: 'right',
        field: 'numberOfActiveLicenses',
        headerName: '# Active Licenses',
        maxWidth: 150,
      },
      {
        align: 'right',
        field: 'numberOfComplimentaryActiveLicenses',
        headerName: '# Comp. Licenses',
        maxWidth: 150,
      },
      {
        align: 'right',
        field: 'numberOfUnredeemedLicenses',
        headerName: '# Unredeemed Licenses',
        minWidth: 180,
      },
      {
        align: 'right',
        field: 'numberOfOrganizations',
        headerName: '# Organizations',
        maxWidth: 150,
      },
      {
        align: 'right',
        field: 'districtValue',
        headerName: 'Annual Value',
        maxWidth: 140,
        valueFormatter: (params) =>
          displayPriceStringFromDollarAmount(convertCentsToDollars(params.value)),
        valueGetter: (params) => params.row.numberOfActiveLicenses * params.row.licensePriceInCents,
      },
      {
        align: 'right',
        field: 'districtValuePercentage',
        headerName: 'Annual Value %',
        maxWidth: 140,
        valueFormatter: (params) => `${(params.value as number).toFixed(2)}%`,
        valueGetter: (params) => {
          const totalValueInCents = params.row.numberOfActiveLicenses
            * params.row.licensePriceInCents;

          return Math.round((totalValueInCents / totalDistrictValueInCents) * 10_000) / 100;
        },
      },
      {
        align: 'right',
        field: 'totalPaidLifetimeInCents',
        headerName: 'Total Paid Lifetime',
        maxWidth: 140,
        valueFormatter: (params) =>
          displayPriceStringFromDollarAmount(convertCentsToDollars(params.value)),
        valueGetter: (params) => params.row.totalPaidLifetimeInCents,
      },
      dateColumn({
        field: 'latestPaymentDate',
        headerName: 'Latest Payment Date',
      }),
      {
        field: 'taxExemptFormS3Key',
        headerName: 'Tax-Exempt',
        renderCell: (params) => {
          switch (params.value) {
            case taxExemptValues.na:
              return 'N/A';
            case taxExemptValues.yes:
              return (
                <CheckIcon
                  fontSize="small"
                  htmlColor={theme.palette.text.secondary}
                />
              );
            case taxExemptValues.no:
            default:
              return '';
          }
        },
        type: 'singleSelect',
        valueGetter: (params) => {
          const stateRequiresSalesTax = doesStateRequireSalesTax(parseInt(params.row.stateId, 10));

          if (!stateRequiresSalesTax) {
            return taxExemptValues.na;
          }

          // If state requires sales tax, return true
          return params.row.taxExemptFormS3Key ? taxExemptValues.yes : taxExemptValues.no;
        },
        valueOptions: taxExemptOptions,
      },
      ...(extraColumns ?? []),
    ];

    return columns;
  }, [
    extraColumns,
    totalDistrictValueInCents,
  ]);
};
